<script setup lang="ts">
  import { JobApplicationFooBar, MpFile, ProfileCv } from '@borg/types';
  import { MpText, MpIcon } from '@borg/ui';

  const VIEW_ICON_NAME = 'information';
  const fileToBeDownloaded = ref<MpFile>();

  const { application } = defineProps<{
    application?: JobApplicationFooBar;
  }>();

  const app = computed(() => ({
    fullName: application?.data?.firstName + ' ' + application?.data?.lastName,
    email: application?.data?.email,
    phone: application?.data?.phone,
    nationality: application?.data?.nationality,
    coverLetter: application?.data?.coverLetter,
    profileCvs: application?.data?.profileCvs.reduce<ProfileCv[]>((acc, cv) => {
      const selected = application?.profileCvs.find((c) => c.id === cv);
      return selected ? [...acc, selected] : acc;
    }, []),
    cvs: application?.data?.cvs.reduce<MpFile[]>((acc, cv) => {
      const selected = application?.cvs.find((c) => c.id === cv);
      return selected ? [...acc, selected] : acc;
    }, []),
    documents: application?.data?.documents.reduce<MpFile[]>((acc, doc) => {
      const docItem = application?.documents.find((d) => d.id === doc);
      return docItem ? [...acc, docItem] : acc;
    }, []),
    questionnaire: application?.questionnaire?.questions.map((q) => ({
      id: q.id,
      question: q.question,
      answers: application?.data.questions.find((qq) => qq.id === q.id)?.answers,
    })),
  }));

  async function onOpenDownloadModal(file: MpFile) {
    fileToBeDownloaded.value = file;
  }

  function onDocumentPreview(file: MpFile) {
    onOpenDownloadModal(file);
  }
</script>

<template>
  <table class="grid">
    <tbody>
      <tr>
        <td>
          <MpText shade="40">
            {{ $t('JOB_APPLICATION_OVERVIEW.CONTACT_INFO_SECTION_TITLE') }}
          </MpText>
        </td>
        <td class="applicant-contact">
          <MpText
            as="h6"
            no-margin
          >
            {{ app.fullName }}
          </MpText>
          <MpText
            v-if="app.email"
            no-margin
          >
            {{ app.email }}
          </MpText>
          <MpText
            v-if="app.phone?.number"
            no-margin
          >
            {{ `+${app.phone.countryCode} ${app.phone.number}` }}
          </MpText>
        </td>
      </tr>
      <tr v-if="application?.profileCvs.length || application?.cvs.length">
        <td>
          <MpText shade="40">
            {{ $t('JOB_APPLICATION_OVERVIEW.BIOGRAPHY_SECTION_TITLE') }}
          </MpText>
        </td>
        <td>
          <div
            v-for="cv in app.profileCvs"
            :key="cv.id"
            class="profile-cv-item"
          >
            <MpText
              no-margin
              as="h6"
            >
              {{ cv.name }}
            </MpText>
          </div>
          <div
            v-for="cv in app.cvs"
            :key="cv.id"
            class="cv-item"
            @click="() => onDocumentPreview(cv)"
          >
            <MpText
              no-margin
              as="h6"
            >
              {{ cv.name }}
            </MpText>
            <MpIcon
              size="sm"
              :icon="VIEW_ICON_NAME"
            />
          </div>
        </td>
      </tr>
      <tr v-if="app.coverLetter">
        <td>
          <MpText
            shade="40"
            no-margin
          >
            {{ $t('JOB_APPLICATION_OVERVIEW.COVER_LETTER_SECTION_TITLE') }}
          </MpText>
        </td>
        <td>
          <MpText>{{ app.coverLetter }}</MpText>
        </td>
      </tr>
      <tr v-if="app.nationality">
        <td>
          <MpText
            shade="40"
            no-margin
          >
            {{ $t('JOB_APPLICATION_OVERVIEW.NATIONALITY_SECTION_TITLE') }}
          </MpText>
        </td>
        <td>
          <MpText>{{ app.nationality.name }}</MpText>
        </td>
      </tr>
      <tr v-if="app.documents?.length">
        <td>
          <MpText
            shade="40"
            no-margin
          >
            {{ $t('JOB_APPLICATION_OVERVIEW.OTHER_DOCUMENTS_SECTION_TITLE') }}
          </MpText>
        </td>
        <td>
          <div
            v-for="doc in app.documents"
            :key="doc.id"
            class="doc-item"
            @click="() => onDocumentPreview(doc)"
          >
            <MpText
              as="h6"
              no-margin
            >
              {{ doc.name }}
            </MpText>
            <MpIcon
              size="sm"
              :icon="VIEW_ICON_NAME"
            />
          </div>
        </td>
      </tr>
      <tr
        v-for="question in app.questionnaire"
        :key="question.id"
      >
        <td>
          <MpText
            shade="40"
            no-margin
          >
            {{ question.question }}
          </MpText>
        </td>
        <td>
          <MpText>
            {{ question.answers?.map((answer) => answer.name).join(', ') }}
          </MpText>
        </td>
      </tr>
    </tbody>
  </table>
  <DocumentsListDownloadModal
    :is-open="!!fileToBeDownloaded"
    :file="fileToBeDownloaded"
    @close="fileToBeDownloaded = undefined"
  />
</template>

<style scoped lang="scss">
  .grid {
    border-collapse: collapse;
    width: 100%;

    tr {
      display: flex;
      flex-direction: column;
      border-bottom: 1px solid var(--mp-color-border-c-10);

      &:last-child {
        border-bottom: none;
      }

      @include screen-md {
        display: table-row;
      }

      td {
        vertical-align: top;
        padding: var(--mp-space-60) 0;
        word-break: break-word;

        @include screen-md {
          padding: var(--mp-space-70) 0;
        }

        &:nth-child(1) {
          padding-bottom: 0;
          min-width: 20rem;

          @include screen-md {
            width: 30%;
            padding-bottom: var(--mp-space-70);
            padding-right: var(--mp-space-90);
          }
        }

        &:nth-child(2) {
          padding-top: var(--mp-space-30);

          @include screen-md {
            padding-top: var(--mp-space-70);
          }
        }
      }

      &:first-child > td {
        padding-top: 0;

        &:nth-child(2) {
          padding-top: var(--mp-space-40);

          @include screen-md {
            padding-top: 0;
          }
        }
      }

      &:last-child > td {
        padding-bottom: 0;
      }
    }
  }

  .applicant-contact {
    display: flex;
    flex-direction: column;
    gap: var(--mp-space-20);
  }

  .cv-item,
  .profile-cv-item,
  .doc-item {
    display: flex;
    gap: var(--mp-space-20);
  }

  .cv-item,
  .doc-item {
    cursor: pointer;
    justify-content: space-between;
    align-items: center;
  }

  .doc-item {
    margin-bottom: var(--mp-space-60);

    &:last-child {
      margin-bottom: 0;
    }
  }
</style>
